import type { AbstractTailoredWidgetTemplateEditor } from '@/components/template-widgets/AbstractTailoredWidgetEditorTemplate.class';
import { TailoredWidgetsProvider } from '@/providers/tailored-widgets/tailored-widgets.provider';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { getValueByPath } from '@/utils/toolkit/object/get-value-by-path.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    inputBinding,
    output,
    outputBinding,
    viewChild,
    ViewContainerRef,
    type Signal,
    type Type
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { map, switchMap } from 'rxjs';
import type { TemplateFormChangedEvent, TemplateWidgetConfig } from '../../interfaces/TailoredWidget.interface';
import type { WidgetTemplateConfigChangedEvent } from '../tailored-widget-editor/tailored-widget-config-editor/tailored-widget-config-editor';
import { TemplateWidgetClassStaticProvider } from '../template-widgets/template-widget-class.static.provider';

@Component({
    styleUrls: ['./template-widget-editor.scss'],
    selector: 'template-widget-editor',
    templateUrl: 'template-widget-editor.html',
    imports: [UiSectionComponent, UiSectionContentComponent, TranslatePipe]
})
export class TailoredWidgetTemplateEditorComponent {
    private _tailoredWidgetsProvider = inject(TailoredWidgetsProvider);
    private editorHost = viewChild('editorHost', { read: ViewContainerRef });

    configToEdit = input.required<TemplateWidgetConfig>();
    configChanged = output<WidgetTemplateConfigChangedEvent>();

    templateParams = computed(() => this.configToEdit()?.tailoredWidgetTemplateConfiguration?.templateWidgetConfigurationParams);
    editorComponent: Signal<Type<AbstractTailoredWidgetTemplateEditor<any>> | undefined>;

    constructor() {
        this.editorComponent = toSignal(
            toObservable(this.configToEdit).pipe(
                switchMap(configToEdit =>
                    this._tailoredWidgetsProvider.getTailoredWidgetTemplateById$(
                        configToEdit.tailoredWidgetTemplateConfiguration?.widgetTemplateId
                    )
                ),
                map(widgetTemplate => TemplateWidgetClassStaticProvider.getWidgetEditorFromTailoredWidgetTemplateCode(widgetTemplate.code))
            )
        );

        effect(() => {
            const host = this.editorHost();
            const component = this.editorComponent();
            if (!host || !component) return;
            // The `config` subscription is torn down automatically on ref.destroy().
            host.createComponent(component, {
                bindings: [
                    // Reactive input binding: re-evaluated on change detection, so it also keeps
                    // `formInputs` in sync as `templateParams` changes (replaces the previous effect).
                    inputBinding('formInputs', () => this.templateParams()),
                    outputBinding<TemplateFormChangedEvent>('config', event => this.handleTemplateParamsUpdate(event))
                ]
            });
        });
    }

    private static interpolateToValidJson(expr: string, params?: any, templateMatcher: RegExp = /{{\s?([^{}\s]*)\s?}}/g) {
        if (!expr || !params) {
            return expr;
        }

        return expr.replace(templateMatcher, (_substring: string, key: string) => {
            const interpolatedExpr = getValueByPath(params, key);

            return isNotNullish(interpolatedExpr)
                ? typeof interpolatedExpr === 'string'
                    ? TailoredWidgetTemplateEditorComponent.escapeSpecialCharacter(interpolatedExpr)
                    : interpolatedExpr
                : null;
        });
    }

    private static escapeSpecialCharacter(text: string): string {
        return JSON.stringify(text).slice(1, -1);
    }

    handleTemplateParamsUpdate(formChangedEvent: TemplateFormChangedEvent) {
        const expression = this.configToEdit().tailoredWidgetTemplateConfiguration.templateWidgetConfigurationExpression;

        // Expression could be empty TODO REP-883
        const interpolatedConfigurationExpression: string | null =
            expression && expression.length > 0
                ? TailoredWidgetTemplateEditorComponent.interpolateToValidJson(expression, formChangedEvent.formValues)
                : null;

        const widgetTemplateConfigChangedEvent: WidgetTemplateConfigChangedEvent = {
            isValid: formChangedEvent.isValid,
            forbiddenDashboardModes: [],
            config: {
                ...this.configToEdit(),
                tailoredWidgetTemplateConfiguration: {
                    ...this.configToEdit()?.tailoredWidgetTemplateConfiguration,
                    templateWidgetConfiguration: interpolatedConfigurationExpression
                        ? JSON.parse(interpolatedConfigurationExpression)
                        : formChangedEvent.formValues,
                    templateWidgetConfigurationParams: formChangedEvent.formValues
                }
            }
        };
        this.configChanged.emit(widgetTemplateConfigChangedEvent);
    }
}
